import "./insFacetedFilter.scss";
import { AuxButton, AuxFacetedFilter, AuxFacetedFilterOption, AuxSearchField, AuxToggle } from "@blk/aladdin-react-components-es";
import { AuxButtonTypeEnum, AuxButtonSizeEnum } from "@blk/aladdin-web-components";
import { isEqual } from "lodash";
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MAX_ITEMS_TO_DISPLAY, clearMultiSlider, getChangedFacedFilterOptionValue, getFilterOptionsData, searchFilters } from "./insFacetedFilter.utils";
import { AuxFacetedFilterOptionTypeEnum } from "./types";
import { InsFacetedFilterOption } from "components/ux/ins-faceted-filter-option.tsx/insFacetedFilterOption";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setSmartFilterShowSelectedOnly } from "store/current-dashboard";
import { DetailsPanelType } from "store/current-dashboard/types";
import { getCurrentLocalTime } from "common/date-utils";
import { setQueryFilters } from "components/clickthrough/store";
import { getFilterPropertiesToRemove } from "components/pages/document-service/filterPillUtils";
import { documentGridId } from "components/pages/document-service/types";
import { clearSelectedRows } from "store/grids";
import { filterSelectedOnlyFilters } from "./insFacetedSelectedFilter";
const tryToTrackEvent = (filters, filterLevel) => {
    if (filterLevel !== undefined) {
        let eventName = "";
        switch (filterLevel.valueOf()) {
            case DetailsPanelType.WidgetFilter:
                eventName = appInsightEvents.customEvents.names.WidgetLevelFilter;
                break;
            case DetailsPanelType.DashboardFilter:
                eventName = appInsightEvents.customEvents.names.DashboardLevelFilter;
                break;
            default:
                break;
        }
        if (eventName !== null && eventName.trim() !== "") {
            appInsightsTrackEvent({ name: eventName, properties: { LocalTimestamp: getCurrentLocalTime() } }, filters);
        }
    }
};
const useFilterHandlers = (props, filterValues, setFilterValues, setSearchValue, setShowSelectedOnly) => {
    const dispatch = useDispatch();
    const filterSelection = useSelector((state) => state.DocumentList?.filterSelections ?? {});
    const onClickApply = () => {
        props.onClickApply(filterValues ?? []);
        tryToTrackEvent(filterValues, props.filterLevel);
    };
    const onFacetDataChanged = (e) => {
        const value = getChangedFacedFilterOptionValue(e.detail, filterValues);
        const field = e.detail.eventData?.field;
        const type = e.detail.eventData?.type;
        if (type === AuxFacetedFilterOptionTypeEnum.PickList && e.detail.newData && props.onExpandedStateChanged) {
            const expandedUids = e.detail.newData.filter((item) => item.isExpanded).map((item) => item.uid);
            props.onExpandedStateChanged(field, expandedUids);
        }
        if (value === undefined) {
            return;
        }
        const changedValues = { ...filterValues, [field]: value };
        setFilterValues(changedValues);
        if (props.autoMode) {
            props.onClickApply(changedValues);
        }
    };
    const onClearClicked = (e) => {
        const type = e.detail.eventData?.type;
        const field = e.detail.eventData?.field;
        if (!field) {
            return;
        }
        let changedValues = {};
        let filterNeedToRemove = {};
        switch (type) {
            case AuxFacetedFilterOptionTypeEnum.MultiSlider: {
                const value = clearMultiSlider(e.detail.eventData);
                changedValues = { ...filterValues, [field]: value };
                break;
            }
            case AuxFacetedFilterOptionTypeEnum.DateRange:
                changedValues = { ...filterValues, [field]: { endDate: "", startDate: "", type: "date" } };
                filterNeedToRemove = getFilterPropertiesToRemove({ field, metadata: field, type: "date" });
                break;
            case AuxFacetedFilterOptionTypeEnum.MultiSelect:
                changedValues = { ...filterValues, [field]: [] };
                filterNeedToRemove = getFilterPropertiesToRemove({ field, metadata: field, type: "multiSelect" });
                break;
            default:
                return;
        }
        dispatch(setQueryFilters({ filter: { ...filterSelection, ...filterNeedToRemove } }));
        dispatch(clearSelectedRows(documentGridId));
        setFilterValues(changedValues);
        if (props.autoMode) {
            props.onClickApply(changedValues);
        }
    };
    const onSearchValueChanged = (e) => {
        setSearchValue(e.detail.submitValue.searchValue);
    };
    const onShowSelectedOnly = (e) => {
        setShowSelectedOnly(e.detail.value.checked ?? false);
    };
    return { onClearClicked, onClickApply, onFacetDataChanged, onSearchValueChanged, onShowSelectedOnly };
};
const useSmartFilterShowSelectedOnly = (setShowSelectedOnly) => {
    const dispatch = useDispatch();
    const smartFilterShowSelectedOnly = useSelector((state) => state.currentDashboard.smartFilterShowSelectedOnly);
    useEffect(() => {
        if (smartFilterShowSelectedOnly) {
            setShowSelectedOnly(true);
            dispatch(setSmartFilterShowSelectedOnly(false));
        }
    }, [smartFilterShowSelectedOnly, dispatch, setShowSelectedOnly]);
};
export const InsFacetedFilter = (props) => {
    const [filterValues, setFilterValues] = useState({});
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    useSmartFilterShowSelectedOnly(setShowSelectedOnly);
    let filters = [...props.filters];
    useEffect(() => {
        if (isEqual(props.stateFilterValues, {})) {
            setFilterValues({});
        }
        else {
            setFilterValues(props.stateFilterValues);
        }
        setShowSelectedOnly((value) => value && Boolean(filters.length));
    }, [props.stateFilterValues, props.filters, filters.length]);
    const showSelectedOnlyDisabled = !filters.length;
    if (showSelectedOnly) {
        filters = filterSelectedOnlyFilters(filters, filterValues);
    }
    else {
        filters = [...props.filters];
    }
    if (searchValue?.length) {
        filters = searchFilters(searchValue, filters);
    }
    const { onClearClicked, onClickApply, onFacetDataChanged, onSearchValueChanged, onShowSelectedOnly } = useFilterHandlers(props, filterValues, setFilterValues, setSearchValue, setShowSelectedOnly);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "aux-level-filter__container" },
            React.createElement(AuxSearchField, { className: "aux-level-filter__search-container", type: "regular", size: "regular", placeholder: language.en.filter.search, onSearchValueChanged: onSearchValueChanged }),
            React.createElement("div", { className: "aux-level-filter__search-container--border-bottom" }),
            React.createElement("div", { className: "aux-level-filter__selected-only-container" },
                React.createElement("div", { className: "aux-level-filter__show-selected-container" },
                    React.createElement(AuxToggle, { size: "regular", label: language.en.filter.showSelectedOnly, isChecked: showSelectedOnly, isDisabled: showSelectedOnlyDisabled, onToggleChanged: onShowSelectedOnly }))),
            React.createElement("span", { className: "aux__assistive-text--hidden", "aria-live": "polite" }),
            React.createElement("div", { className: "aux-level-filter__show-selected--border-bottom" })),
        React.createElement("div", { className: "filterContent filterContentInner" },
            React.createElement("div", null,
                React.createElement(AuxFacetedFilter, { isBorderLess: true, type: "accordion" }, filters.map((filterGroup) => React.createElement(AuxFacetedFilterOption, { key: filterGroup.group, isExpanded: filterGroup.isExpanded ?? true, label: filterGroup.label }, filterGroup.data.map((filterOption) => React.createElement(InsFacetedFilterOption, { key: `${filterGroup.group}_${filterOption.field}`, type: filterOption.type, label: filterOption.label, overflow: MAX_ITEMS_TO_DISPLAY, eventData: filterOption, isInnerAccordion: true, isExpanded: filterOption.isExpanded || showSelectedOnly, clear: true, data: getFilterOptionsData(filterOption, filterValues, props.expandedState), onFacetDataChanged: onFacetDataChanged, onClearClicked: onClearClicked }))))))),
        !props.autoMode &&
            React.createElement("div", { className: "filterAction" },
                React.createElement(AuxButton, { label: language.en.filter.clearFilters, onClick: props.onClickCleanFilters, size: AuxButtonSizeEnum.SMALL, type: AuxButtonTypeEnum.SECONDARY }),
                React.createElement(AuxButton, { label: language.en.filter.applyFilters, onClick: onClickApply, size: AuxButtonSizeEnum.SMALL, type: AuxButtonTypeEnum.PRIMARY }))));
};
